import React from "react";

/**
 * Tiny round avatar that falls back to a brand-coloured initials chip when
 * no picture is available. Use everywhere a user's identity is mentioned
 * (threads, replies, reviews, price reports, etc.) so the community starts
 * feeling like real people instead of anonymous handles.
 *
 * Props:
 *  - src: optional image URL / data URL
 *  - name: full name (used for initials fallback)
 *  - size: diameter in px (default 32)
 *  - ringed: thin orange ring (default true)
 */
function initialsFor(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).filter(Boolean).join("") || "?";
}

// 8 deterministic background tints — picks one based on the name hash so each
// camper gets a consistent colour across the app (recognition-friendly).
const PALETTE = [
  "#ff4500", "#0ea5e9", "#22c55e", "#eab308",
  "#a855f7", "#ec4899", "#14b8a6", "#f97316",
];

function tintFor(name) {
  let h = 0;
  const s = name || "";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}

export default function Avatar({ src, name = "", size = 32, ringed = true, className = "", testid }) {
  const style = {
    width: size,
    height: size,
    fontSize: Math.max(10, Math.round(size * 0.36)),
  };
  const ring = ringed ? "border-2 border-[#ff4500]" : "border hairline";
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={style}
        className={`rounded-full object-cover shrink-0 ${ring} ${className}`}
        data-testid={testid}
        loading="lazy"
        decoding="async"
      />
    );
  }
  return (
    <span
      style={{ ...style, background: tintFor(name) }}
      className={`rounded-full inline-flex items-center justify-center font-bold text-white shrink-0 ${ring} ${className}`}
      aria-label={name || "User"}
      data-testid={testid}
    >
      {initialsFor(name)}
    </span>
  );
}
